import { IUserFit } from '@gettactic/api';
import { AVATAR_SIZES } from './UserAvatar';
import { SlidingNodes } from '@cmp-src/components/SlidingNodes/SlidingNodes';
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger
} from '@v2/components/ui/tooltip';
import { cn } from '@v2/lib/utils';
import { useState, useEffect, useRef } from 'react';
import { UserAvatar } from '@v2/components/display/UserAvatar/UserAvatar';

export type UserAvatarStackProps = {
  users: IUserFit[];
  hoverDirection?: 'left' | 'right';
  showRest?: boolean;
  maxVisibleAvatars?: number;
  avatarSize?: AVATAR_SIZES;
};

export const UserAvatarStack = ({
  users,
  hoverDirection = 'left',
  showRest = true,
  maxVisibleAvatars = 3,
  avatarSize = AVATAR_SIZES.ROUNDED_8_8
}: UserAvatarStackProps) => {
  const hasExtraUsers = users.length > 3;
  const [startIndex, setStartIndex] = useState(
    hasExtraUsers ? Math.max(0, users.length - 3) : 0
  );
  const prevUsersRef = useRef<IUserFit[]>();
  const isManualNavigationRef = useRef(false);

  const intervals = useRef<Record<string, NodeJS.Timeout | undefined>>({
    loop: undefined
  });

  // Reset startIndex only when users array changes
  useEffect(() => {
    const prevUsers = prevUsersRef.current;
    prevUsersRef.current = users;

    // Skip if this is from a manual navigation or if users array is the same
    if (isManualNavigationRef.current) {
      isManualNavigationRef.current = false;
      return;
    }

    // If users changed or it's the initial render
    if (!prevUsers || prevUsers.length !== users.length) {
      if (hasExtraUsers) {
        setStartIndex(Math.max(0, users.length - 3));
      } else {
        setStartIndex(0);
      }
    }
  }, [users, hasExtraUsers]);

  // Clear intervals when component unmounts
  useEffect(() => {
    return () => {
      if (intervals.current.loop) clearInterval(intervals.current.loop);
    };
  }, []);

  const visibleUsers = hasExtraUsers
    ? users.slice(startIndex, startIndex + 3)
    : users.slice(0, 3);

  const hiddenUsersCount = users.length > 3 ? users.length - 3 : 0;

  const handleLoopNavigation = () => {
    if (users.length <= 3) return;

    isManualNavigationRef.current = true;

    // Move to previous index, or loop back to the end
    setStartIndex((prev) => {
      const newIndex = prev - 1;
      return newIndex >= 0 ? newIndex : users.length - 3;
    });
  };

  const startLoopInterval = () => {
    // Clear any existing interval
    if (intervals.current.loop) {
      clearInterval(intervals.current.loop);
      intervals.current.loop = undefined;
    }

    // Only set interval if we have more than 3 users
    if (users.length > 3) {
      setTimeout(() => {
        handleLoopNavigation();
      }, 250);
      intervals.current.loop = setInterval(() => {
        handleLoopNavigation();
      }, 1000);
    }
  };

  const stopLoopInterval = () => {
    if (intervals.current.loop) {
      clearInterval(intervals.current.loop);
      intervals.current.loop = undefined;
    }
  };

  const avatars = visibleUsers.map((user) => (
    <TooltipProvider key={user.id}>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <div>
            <UserAvatar
              user={user}
              size={'s'}
              className={cn(
                users.length > 1 ? 'outline outline-2 outline-white' : ''
              )}
            />
          </div>
        </TooltipTrigger>
        <TooltipPortal>
          <TooltipContent>{user.name + (user.roles?.includes('deleted') ? ' (inactive)' : '')}</TooltipContent>
        </TooltipPortal>
      </Tooltip>
    </TooltipProvider>
  ));

  const restText = showRest && users.length > maxVisibleAvatars && (
    <div
      className={cn(
        'absolute top-[0.25rem] z-0 flex cursor-pointer items-center justify-center whitespace-nowrap align-center text-neutral-400 text-xs transition-all duration-300 group-hover:text-neutral-600',
        hoverDirection === 'left'
          ? '-left-5 group-hover:-left-[4.3rem]'
          : 'left-5 group-hover:left-[4.3rem]'
      )}
      onClick={handleLoopNavigation}
      onMouseEnter={startLoopInterval}
      onMouseLeave={stopLoopInterval}
    >
      + {hiddenUsersCount}{' '}
      <span className="ml-1 text-white text-xs transition-all duration-300 group-hover:text-neutral-600">
        more
      </span>
    </div>
  );

  return (
    <div className="group relative">
      {hoverDirection === 'left' && restText}

      <SlidingNodes
        hoverDirection={hoverDirection}
        spacingClass="-space-x-3"
        hoverSpacing={2}
        duration={200}
        hoverToFront={true}
        className="relative z-docked transition-all duration-300"
      >
        {avatars}
      </SlidingNodes>

      {hoverDirection === 'right' && restText}
    </div>
  );
};
